// Arc (Circle L1) Uniswap v4 pozisyonundan arayüzsüz çıkış.
//
// Uniswap'ın kendi arayüzü pozisyonları zincirden değil kendi indeksleyicisinden
// okuyor; Arc'ta yeni havuzlar oraya günlerce düşmüyor. Likidite zincirde duruyor,
// tek gereken PositionManager.modifyLiquidities çağrısı — burada onu elle kuruyoruz.
//
// İmzalayan KULLANICIDIR: burada yalnız calldata üretilir, simüle edilir ve
// cüzdana gönderilir. Özel anahtar hiçbir yerde tutulmaz.
//
// v4 aksiyon kodları (v4-periphery Actions.sol):
//   0x01 DECREASE_LIQUIDITY · 0x03 BURN_POSITION · 0x11 TAKE_PAIR

export const ARC_CHAIN_ID = '0x13b2';                                        // 5042
export const ARC_RPC = 'https://rpc.mainnet.arc.io';
export const ARC_POSITION_MANAGER = '0x6049c9a0e26405c0985f9e3685c87d0ae917f82b';
const SEL_MODIFY = '0xdd46508f';   // modifyLiquidities(bytes,uint256)
const SEL_OWNER  = '0x6352211e';   // ownerOf(uint256)
const SEL_POSINF = '0x7ba03aad';   // getPoolAndPositionInfo(uint256)
const SEL_LIQ    = '0x1efeed33';   // getPositionLiquidity(uint256)

declare global {
  interface Window { ethereum?: any }
}

// ── ABI kodlama (minimal — kütüphane eklemeden) ────────────────────────────────
const W = (v: bigint | number | string) => BigInt(v).toString(16).padStart(64, '0');

const encBytes = (hex: string) => {                    // hex: 0x'siz
  const pad = (64 - (hex.length % 64)) % 64;
  return W(hex.length / 2) + hex + '0'.repeat(pad);
};

const encBytesArr = (items: string[]) => {
  const tails = items.map(encBytes);
  let cur = 32 * items.length;
  const offs: number[] = [];
  for (const t of tails) { offs.push(cur); cur += t.length / 2; }
  return W(items.length) + offs.map(W).join('') + tails.join('');
};

/**
 * liquidity === null  →  BURN_POSITION (tamamı + birikmiş ücret, NFT yakılır)
 * liquidity  >  0     →  DECREASE_LIQUIDITY (kısmi, NFT durur)
 * Her ikisinde de TAKE_PAIR ile iki token `to` adresine çekilir.
 */
export function buildExitCalldata(opts: {
  tokenId: bigint; liquidity: bigint | null;
  currency0: string; currency1: string; to: string; deadline: number;
}): string {
  const { tokenId, liquidity, currency0, currency1, to, deadline } = opts;
  const burn = liquidity === null;
  const actions = (burn ? '03' : '01') + '11';
  const p1 = burn
    ? W(tokenId) + W(0) + W(0) + W(0x80) + W(0)                    // id, a0Min, a1Min, hookData
    : W(tokenId) + W(liquidity) + W(0) + W(0) + W(0xa0) + W(0);    // id, liq, a0Min, a1Min, hookData
  const p2 = W(BigInt(currency0)) + W(BigInt(currency1)) + W(BigInt(to));
  const encAct = encBytes(actions);
  const unlock = W(0x40) + W(0x40 + encAct.length / 2) + encAct + encBytesArr([p1, p2]);
  return SEL_MODIFY + W(0x40) + W(deadline) + encBytes(unlock);
}

// ── Zincir okuma ───────────────────────────────────────────────────────────────
const words = (h: string): string[] => h.slice(2).match(/.{64}/g) || [];
const toInt = (w: string) => BigInt('0x' + w);
const toAddr = (w: string) => '0x' + w.slice(24);
const sInt = (v: bigint, bits: number) =>
  v >= (1n << BigInt(bits - 1)) ? v - (1n << BigInt(bits)) : v;

// ── Cüzdan keşfi (EIP-6963) ────────────────────────────────────────────────────
// window.ethereum'a GÜVENİLMEZ: son kurulan cüzdan (Trust, Phantom…) onu ele
// geçiriyor ve çoğu `isMetaMask` bayrağını da taklit ediyor. EIP-6963'te her
// cüzdan kendini rdns ile duyurur; hangisini kullanacağımızı biz seçeriz.
export interface WalletChoice { uuid: string; name: string; rdns: string; icon?: string; provider: any }

const _found = new Map<string, WalletChoice>();
let _picked: WalletChoice | null = null;

export function discoverWallets(waitMs = 350): Promise<WalletChoice[]> {
  if (typeof window === 'undefined') return Promise.resolve([]);
  return new Promise(resolve => {
    const on = (ev: any) => {
      const d = ev?.detail;
      if (!d?.info?.rdns || !d?.provider) return;
      _found.set(d.info.rdns, {
        uuid: d.info.uuid, name: d.info.name, rdns: d.info.rdns, icon: d.info.icon, provider: d.provider,
      });
    };
    window.addEventListener('eip6963:announceProvider', on as any);
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', on as any);
      // EIP-6963 desteklemeyen tek cüzdan varsa eski yolu da listeye kat.
      if (!_found.size && window.ethereum) {
        const legacy = Array.isArray(window.ethereum.providers) && window.ethereum.providers.length
          ? window.ethereum.providers : [window.ethereum];
        legacy.forEach((p: any, i: number) => {
          const name = p.isMetaMask && !p.isTrust && !p.isTrustWallet ? 'MetaMask'
            : p.isTrust || p.isTrustWallet ? 'Trust Wallet'
            : p.isRabby ? 'Rabby' : p.isCoinbaseWallet ? 'Coinbase Wallet' : `Cüzdan ${i + 1}`;
          _found.set('legacy:' + i, { uuid: 'legacy:' + i, name, rdns: 'legacy:' + i, provider: p });
        });
      }
      const list = [...  _found.values()];
      if (!_picked) _picked = list.find(w => w.rdns === 'io.metamask')
        || list.find(w => /metamask/i.test(w.name)) || list[0] || null;
      resolve(list);
    }, waitMs);
  });
}

export const pickWallet = (w: WalletChoice) => { _picked = w; };
export const pickedWallet = () => _picked;

function provider() {
  return _picked?.provider || null;
}

export const hasWallet = () => !!_picked;

const call = async (to: string, data: string) => {
  const p = provider();
  if (p) return p.request({ method: 'eth_call', params: [{ to, data }, 'latest'] }) as Promise<string>;
  // Cüzdan yoksa salt okuma için açık RPC (CORS açıksa çalışır)
  const r = await fetch(ARC_RPC, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method: 'eth_call', params: [{ to, data }, 'latest'] }),
  });
  const d = await r.json();
  if (d.error) throw new Error(d.error.message);
  return d.result as string;
};

export interface V4Position {
  owner: string; currency0: string; currency1: string;
  fee: number; tickSpacing: number; hooks: string;
  tickLower: number; tickUpper: number; liquidity: bigint;
}

export async function readV4Position(tokenId: bigint): Promise<V4Position> {
  const arg = W(tokenId);
  const owner = toAddr(words(await call(ARC_POSITION_MANAGER, SEL_OWNER + arg))[0]);
  const pi = words(await call(ARC_POSITION_MANAGER, SEL_POSINF + arg));
  const info = toInt(pi[5]);
  const liquidity = toInt(words(await call(ARC_POSITION_MANAGER, SEL_LIQ + arg))[0]);
  return {
    owner,
    currency0: toAddr(pi[0]),
    currency1: toAddr(pi[1]),
    fee: Number(toInt(pi[2])),
    tickSpacing: Number(toInt(pi[3])),
    hooks: toAddr(pi[4]),
    // PositionInfo: 200 bit poolId | 24 bit tickUpper | 24 bit tickLower | 8 bit subscriber
    tickUpper: Number(sInt((info >> 32n) & 0xffffffn, 24)),
    tickLower: Number(sInt((info >> 8n) & 0xffffffn, 24)),
    liquidity,
  };
}

// ── Cüzdan ─────────────────────────────────────────────────────────────────────
export async function connectArc(): Promise<string> {
  const p = provider();
  if (!p) throw new Error('Tarayıcıda cüzdan eklentisi bulunamadı (MetaMask / Rabby).');
  const accts: string[] = await p.request({ method: 'eth_requestAccounts' });
  if (!accts?.length) throw new Error('Cüzdan hesabı alınamadı.');
  const cid = await p.request({ method: 'eth_chainId' });
  if (cid !== ARC_CHAIN_ID) {
    try {
      await p.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: ARC_CHAIN_ID }] });
    } catch (e: any) {
      if (e?.code !== 4902) throw new Error('Cüzdanı Arc mainnet (5042) ağına alın.');
      await p.request({
        method: 'wallet_addEthereumChain',
        params: [{
          chainId: ARC_CHAIN_ID, chainName: 'Arc',
          nativeCurrency: { name: 'USD Coin', symbol: 'USDC', decimals: 18 },
          rpcUrls: [ARC_RPC], blockExplorerUrls: ['https://explorer.arc.io'],
        }],
      });
    }
  }
  return accts[0];
}

/** Revert edecek bir işlemi cüzdana HİÇ göndermemek için önce simüle et. */
export async function simulateExit(from: string, data: string): Promise<bigint> {
  const p = provider();
  if (!p) throw new Error('Cüzdan yok.');
  await p.request({ method: 'eth_call', params: [{ from, to: ARC_POSITION_MANAGER, data, value: '0x0' }, 'latest'] });
  const gas: string = await p.request({
    method: 'eth_estimateGas',
    params: [{ from, to: ARC_POSITION_MANAGER, data, value: '0x0' }],
  });
  return BigInt(gas);
}

export async function sendExit(from: string, data: string, gas: bigint): Promise<string> {
  const p = provider();
  if (!p) throw new Error('Cüzdan yok.');
  return p.request({
    method: 'eth_sendTransaction',
    params: [{ from, to: ARC_POSITION_MANAGER, data, value: '0x0', gas: '0x' + ((gas * 13n) / 10n).toString(16) }],
  });
}

export async function waitReceipt(hash: string, tries = 60): Promise<any> {
  const p = provider();
  for (let i = 0; i < tries; i++) {
    const r = await p.request({ method: 'eth_getTransactionReceipt', params: [hash] });
    if (r) return r;
    await new Promise(res => setTimeout(res, 2000));
  }
  return null;
}

/** 'arc-v4:227532' → 227532n · v4 değilse null */
export const v4TokenId = (positionId: string): bigint | null => {
  const [kind, ref] = positionId.split(':');
  if (kind !== 'arc-v4' || !ref) return null;
  try { return BigInt(ref); } catch { return null; }
};
